import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { OpticalJobStatus, type OpticalJobStatus as JobStatus } from '@optiflow/types';
import { cn } from '@/utils/cn';
import { JOB_STEPS } from '../tracking/jobs';

/**
 * Where a job stands among its six steps.
 *
 * A list a screen reader reads in order, the current step marked as such; done steps carry a tick and the word for it. On a phone the labels give way to one "Step 3 of 6" line so nothing scrolls sideways. A cancelled job shows no progress at all: it stopped before the lab.
 */
export function JobProgress({ status }: { status: JobStatus }) {
  const { t } = useTranslation();
  if (status === OpticalJobStatus.CANCELLED) return null;
  const index = JOB_STEPS.indexOf(status);

  return (
    <nav aria-label={t('opticalOrders.tracking.progress')} className="space-y-2">
      <p className="text-muted text-sm sm:hidden">
        {t('opticalOrders.tracking.stepOf', {
          current: index + 1,
          total: JOB_STEPS.length,
          name: t(`sales.opticalJobs.status.${status}`),
        })}
      </p>
      <ol className="grid grid-cols-6 gap-1.5 sm:gap-2">
        {JOB_STEPS.map((step, position) => {
          const done = position < index || status === OpticalJobStatus.DELIVERED;
          const current = position === index;
          return (
            <li
              key={step}
              aria-current={current ? 'step' : undefined}
              className={cn(
                'min-w-0 border-t-4 pt-2 text-xs sm:text-sm',
                current
                  ? 'border-brand-600 text-ink font-medium'
                  : done
                    ? 'border-emerald-500 text-ink'
                    : 'border-line text-muted',
              )}
            >
              <span className="flex items-center gap-1">
                {done ? (
                  <>
                    <Check className="size-3.5 shrink-0 text-emerald-600" aria-hidden />
                    <span className="sr-only">{t('opticalOrders.tracking.stepDone')}</span>
                  </>
                ) : null}
                <span className="hidden truncate sm:block">
                  {t(`sales.opticalJobs.status.${step}`)}
                </span>
                <span className="sr-only sm:hidden">{t(`sales.opticalJobs.status.${step}`)}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
